import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { MatHeaderCell, MatTableModule } from '@angular/material/table';
import { FilterMenu } from '../filter-menu/filter-menu';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { IconType } from '@kinomap-dev/ng-ui-lib/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ColorService } from '../../../services/color.service';
import { FilterService } from '../../../services/filters.service';
import { FilterSortDirection } from '../../../types/filter.type';

@Component({
  selector: 'th[app-column-header]',
  imports: [IconComponent, MatTableModule, CdkMenuTrigger, FilterMenu],
  template: `
    <div class="header-cell" [class.sortable]="isSortable()">
      <p
        (click)="changeMode()"
        [style]="'color:' + textColor() + '; marginRight:' + (!showSortIcon() ? '28px' : '0')"
      >
        {{ title() }}
      </p>
      @if (showSortIcon()) {
        <kin-icon
          (iconClick)="changeMode()"
          class="sort-icon"
          [color]="sortIconColor()"
          [name]="sortIcon() ? sortIcon()! : 'sort_table'"
          [size]="20"
        />
      }
      <div
        style="marginLeft:auto"
        class="filter-icon-background"
        [cdkMenuTriggerFor]="filterMenuTpl"
        [cdkMenuPosition]="menuPositions"
        (cdkMenuOpened)="filterMenu.set(true)"
        (cdkMenuClosed)="filterMenu.set(false)"
        (mouseenter)="filterIconHover.set(true)"
        (mouseleave)="filterIconHover.set(false)"
        [style]="'background:' + filterIconBackgroundColor()"
      >
        @if (activeFilterMenu()) {
          <span class="filter-dot"></span>
        }
        <kin-icon class="filter-icon" name="align_right" [color]="fiterIconColor()" />
      </div>
    </div>
    <ng-template #filterMenuTpl>
      <app-filter-menu [column]="column()" />
    </ng-template>
  `,
  styleUrl: './column-header.scss',
  hostDirectives: [
    MatHeaderCell,
    { directive: CdkDrag, inputs: ['cdkDragDisabled'], outputs: ['cdkDragEnded'] },
  ],
  host: {
    '(mouseenter)': 'this.isHovered.set(true)',
    '(mouseleave)': 'this.isHovered.set(false)',
  },
})
export class ColumnHeader {
  readonly filterService: FilterService = inject(FilterService);
  readonly colorService: ColorService = inject(ColorService);

  menuPositions: ConnectedPosition[] = [
    {
      originX: 'start', // point d'attache sur le trigger (start | center | end)
      originY: 'bottom', // point d'attache sur le trigger (top | center | bottom)
      overlayX: 'start', // point d'attache sur le menu
      overlayY: 'top',
    },
  ];

  title: InputSignal<string> = input.required();
  column: InputSignal<string> = input.required();

  currentSortMode: Signal<FilterSortDirection | undefined> = computed(() =>
    this.filterService.getActiveSortDirection(this.column()),
  );
  isSortable: Signal<boolean> = computed(
    () => this.filterService.filter()[this.column()]?.sortable !== false,
  );

  sortIcon: Signal<IconType | undefined> = computed(() => {
    const currentSortMode: FilterSortDirection | undefined = this.currentSortMode();

    return currentSortMode ? this.sortIconMap[currentSortMode] : undefined;
  });
  showSortIcon: Signal<boolean> = computed(
    () => this.isSortable() && (this.sortIcon() !== undefined || this.isHovered()),
  );

  sortIconColor: Signal<string> = computed(() => {
    if (this.isHovered()) return this.colorService.brandSecondary();
    return this.colorService.neutral900();
  });

  isHovered: WritableSignal<boolean> = signal(false);

  filterIconHover: WritableSignal<boolean> = signal(false);

  filterMenu: WritableSignal<boolean> = signal(false);
  activeFilterMenu: Signal<boolean> = computed(
    () =>
      this.filterService.hasActiveFilter(this.column()) ||
      this.filterService.getActiveSortDirection(this.column()) !== undefined,
  );

  textColor: Signal<string> = computed(() => {
    if (this.isHovered() && this.isSortable()) return this.colorService.brandSecondary();
    return this.currentSortMode() ? this.colorService.neutral900() : this.colorService.neutral700();
  });

  changeMode(): void {
    if (!this.isSortable()) return;

    this.filterService.toggleFilterSortDirection(this.column());
  }

  private readonly sortIconMap: Record<FilterSortDirection, IconType> = {
    asc: 'sort_arrow_top',
    desc: 'sort_arrow_bottom',
  };

  fiterIconColor: Signal<string> = computed(() => {
    if (this.filterIconHover()) return this.colorService.neutral900();
    return this.colorService.neutral500();
  });

  filterIconBackgroundColor: Signal<string> = computed(() => {
    if (this.filterMenu()) return this.colorService.backgroundColor();
    if (this.filterIconHover()) return this.colorService.brandSecondary200();
    return 'transparent';
  });

  constructor() {
    inject(CdkDrag).boundaryElement = '.table-scroll-x';
  }
}
